import React, { useEffect, useMemo, useState } from "react"
import { accessFromProps } from "../lib/book-access"
import { BookShell } from "../components/book-shell"
import { PageHeader } from "../components/page-header"
import { Button } from "../components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card"
import { cn } from "../lib/utils"
import { appConfirm } from "../lib/app-dialogs"
import { PayrollPanel } from "../components/payroll-panel"
import { usePageLoading } from "../lib/page-loading"

function csrfToken() {
  return document.querySelector("meta[name='csrf-token']")?.getAttribute("content") || ""
}

async function fetchJson(url, options = {}) {
  const res = await fetch(url, {
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
      "X-CSRF-Token": csrfToken(),
      ...(options.headers || {})
    },
    ...options
  })

  const contentType = res.headers.get("content-type") || ""
  const data = contentType.includes("application/json") ? await res.json() : null
  if (!res.ok) {
    const message = data?.message || (data?.error ? String(data.error) : `Request failed (${res.status})`)
    const details = data?.details ? JSON.stringify(data.details) : ""
    throw new Error(details ? `${message}: ${details}` : message)
  }
  return data
}

function centsToAmount(cents) {
  if (cents === null || cents === undefined) return ""
  return (Number(cents) / 100).toFixed(2)
}

function amountToCents(value) {
  const n = Number.parseFloat(String(value || "0"))
  if (Number.isNaN(n)) return 0
  return Math.round(n * 100)
}

function TextInput({ label, value, onChange, type = "text", placeholder }) {
  return (
    <label className="grid gap-0.5 text-xs">
      <span className="text-muted-foreground">{label}</span>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-full rounded-lg border border-input bg-card px-2.5 py-1.5 text-sm text-foreground outline-none transition-shadow focus:border-primary focus:ring-2 focus:ring-ring/20"
      />
    </label>
  )
}

function SelectInput({ label, value, onChange, options }) {
  return (
    <label className="grid gap-0.5 text-xs">
      <span className="text-muted-foreground">{label}</span>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full rounded-lg border border-input bg-card px-2.5 py-1.5 text-sm text-foreground outline-none transition-shadow focus:border-primary focus:ring-2 focus:ring-ring/20"
      >
        <option value="">Select…</option>
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    </label>
  )
}

function nextDocNumber(items, fieldName, prefix) {
  let max = 0
  items.forEach((item) => {
    const raw = String(item[fieldName] || "")
    const match = raw.match(/(\d+)\s*$/)
    if (!match) return
    const n = Number.parseInt(match[1], 10)
    if (!Number.isNaN(n)) max = Math.max(max, n)
  })
  return `${prefix}${String(max + 1).padStart(4, "0")}`
}

const REPORT_SECTIONS = new Set(["trial-balance", "general-ledger", "income-statement", "gstr1-sales", "gstr2-purchases"])

function BookkeepingSettingsPanel() {
  const [accounts, setAccounts] = useState([])
  const [org, setOrg] = useState(null)
  const [loading, setLoading] = useState(true)
  usePageLoading(loading)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState("")

  useEffect(() => {
    let alive = true
    ;(async () => {
      try {
        setLoading(true)
        setError("")
        const [coa, settings] = await Promise.all([
          fetchJson("/api/accounting/chart_accounts"),
          fetchJson("/api/accounting/bookkeeping_settings")
        ])
        if (!alive) return
        setAccounts(coa || [])
        setOrg(settings?.organization || null)
      } catch (e) {
        if (alive) setError(e.message || "Failed to load")
      } finally {
        if (alive) setLoading(false)
      }
    })()
    return () => {
      alive = false
    }
  }, [])

  const opts = accounts.map((a) => ({ value: String(a.id), label: `${a.code} — ${a.name}` }))

  function setField(name, value) {
    setOrg((prev) => {
      if (!prev) return prev
      if (value === "") return { ...prev, [name]: null }
      const n = Number.parseInt(value, 10)
      return { ...prev, [name]: Number.isNaN(n) ? null : n }
    })
  }

  async function onSave(e) {
    e.preventDefault()
    if (!org) return
    try {
      setSaving(true)
      setError("")
      const body = JSON.stringify({
        organization: {
          enable_vendor_quote_reselling: Boolean(org.enable_vendor_quote_reselling),
          ar_account_id: org.ar_account_id,
          revenue_account_id: org.revenue_account_id,
          ap_account_id: org.ap_account_id,
          expense_account_id: org.expense_account_id,
          cash_account_id: org.cash_account_id,
          gst_output_cgst_account_id: org.gst_output_cgst_account_id,
          gst_output_sgst_account_id: org.gst_output_sgst_account_id,
          gst_output_igst_account_id: org.gst_output_igst_account_id,
          gst_input_cgst_account_id: org.gst_input_cgst_account_id,
          gst_input_sgst_account_id: org.gst_input_sgst_account_id,
          gst_input_igst_account_id: org.gst_input_igst_account_id,
          salary_expense_account_id: org.salary_expense_account_id,
          salaries_payable_account_id: org.salaries_payable_account_id,
        },
      })
      const data = await fetchJson("/api/accounting/bookkeeping_settings", { method: "PATCH", body })
      setOrg(data?.organization || org)
    } catch (err) {
      setError(err.message || "Save failed")
    } finally {
      setSaving(false)
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Bookkeeping (default GL)</CardTitle>
      </CardHeader>
      <CardContent className="grid gap-4">
        <p className="text-xs text-muted-foreground">
          Map chart accounts used when posting sales invoices, customer receipts, vendor bills, vendor payments, and cash expenses.
          For GST line items, map output tax (sales) and input ITC (purchases); posting splits revenue/expense net of tax when line CGST/SGST/IGST
          amounts exist.
        </p>
        {error ? <div className="rounded border border-red-200 bg-red-50 px-2 py-1.5 text-xs text-red-700">{error}</div> : null}
        {loading ? (
          <p className="text-xs text-muted-foreground">Loading…</p>
        ) : org ? (
          <form onSubmit={onSave} className="grid gap-4 md:grid-cols-2">
            <label className="md:col-span-2 flex items-start gap-2 rounded-xl border border-border/90 bg-card px-3 py-2 text-xs shadow-sm">
              <input
                type="checkbox"
                className="mt-0.5"
                checked={Boolean(org.enable_vendor_quote_reselling)}
                onChange={(e) =>
                  setOrg((prev) => (prev ? { ...prev, enable_vendor_quote_reselling: e.target.checked } : prev))
                }
              />
              <span>
                <span className="font-medium text-foreground">Vendor quote reselling (optional)</span>
                <span className="mt-0.5 block text-muted-foreground">
                  Record quotations received from vendors and generate your own outbound quotes (with structured quote numbers) for third-party customers.
                </span>
              </span>
            </label>
            <SelectInput
              label="Accounts receivable"
              value={org.ar_account_id ? String(org.ar_account_id) : ""}
              onChange={(v) => setField("ar_account_id", v)}
              options={opts}
            />
            <SelectInput
              label="Revenue"
              value={org.revenue_account_id ? String(org.revenue_account_id) : ""}
              onChange={(v) => setField("revenue_account_id", v)}
              options={opts}
            />
            <SelectInput
              label="Accounts payable"
              value={org.ap_account_id ? String(org.ap_account_id) : ""}
              onChange={(v) => setField("ap_account_id", v)}
              options={opts}
            />
            <SelectInput
              label="Expense (purchases)"
              value={org.expense_account_id ? String(org.expense_account_id) : ""}
              onChange={(v) => setField("expense_account_id", v)}
              options={opts}
            />
            <SelectInput
              label="Cash / bank"
              value={org.cash_account_id ? String(org.cash_account_id) : ""}
              onChange={(v) => setField("cash_account_id", v)}
              options={opts}
            />
            <div className="md:col-span-2 mt-1 text-[12px] font-medium text-muted-foreground">Payroll</div>
            <SelectInput
              label="Salary expense"
              value={org.salary_expense_account_id ? String(org.salary_expense_account_id) : ""}
              onChange={(v) => setField("salary_expense_account_id", v)}
              options={opts}
            />
            <SelectInput
              label="Salaries payable"
              value={org.salaries_payable_account_id ? String(org.salaries_payable_account_id) : ""}
              onChange={(v) => setField("salaries_payable_account_id", v)}
              options={opts}
            />
            <div className="md:col-span-2 mt-1 text-[12px] font-medium text-muted-foreground">GST — output (sales)</div>
            <SelectInput
              label="Output CGST payable"
              value={org.gst_output_cgst_account_id ? String(org.gst_output_cgst_account_id) : ""}
              onChange={(v) => setField("gst_output_cgst_account_id", v)}
              options={opts}
            />
            <SelectInput
              label="Output SGST payable"
              value={org.gst_output_sgst_account_id ? String(org.gst_output_sgst_account_id) : ""}
              onChange={(v) => setField("gst_output_sgst_account_id", v)}
              options={opts}
            />
            <SelectInput
              label="Output IGST payable"
              value={org.gst_output_igst_account_id ? String(org.gst_output_igst_account_id) : ""}
              onChange={(v) => setField("gst_output_igst_account_id", v)}
              options={opts}
            />
            <div className="md:col-span-2 mt-1 text-[12px] font-medium text-muted-foreground">GST — input ITC (purchases)</div>
            <SelectInput
              label="Input CGST (ITC)"
              value={org.gst_input_cgst_account_id ? String(org.gst_input_cgst_account_id) : ""}
              onChange={(v) => setField("gst_input_cgst_account_id", v)}
              options={opts}
            />
            <SelectInput
              label="Input SGST (ITC)"
              value={org.gst_input_sgst_account_id ? String(org.gst_input_sgst_account_id) : ""}
              onChange={(v) => setField("gst_input_sgst_account_id", v)}
              options={opts}
            />
            <SelectInput
              label="Input IGST (ITC)"
              value={org.gst_input_igst_account_id ? String(org.gst_input_igst_account_id) : ""}
              onChange={(v) => setField("gst_input_igst_account_id", v)}
              options={opts}
            />
            <div className="md:col-span-2 flex items-center gap-2">
              <Button type="submit" disabled={saving}>
                {saving ? "…" : "Save"}
              </Button>
            </div>
          </form>
        ) : (
          <p className="text-xs text-muted-foreground">Organization not found.</p>
        )}
      </CardContent>
    </Card>
  )
}

function FiscalPeriodsPanel() {
  const [rows, setRows] = useState([])
  const [loading, setLoading] = useState(true)
  usePageLoading(loading)
  const [error, setError] = useState("")
  const [savingId, setSavingId] = useState(null)

  async function load() {
    try {
      setLoading(true)
      setError("")
      const data = await fetchJson("/api/accounting/fiscal_periods")
      setRows(data || [])
    } catch (e) {
      setError(e.message || "Failed to load")
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    load()
  }, [])

  async function setClosed(id, closed) {
    setSavingId(id)
    try {
      setError("")
      await fetchJson(`/api/accounting/fiscal_periods/${id}`, {
        method: "PATCH",
        body: JSON.stringify({ fiscal_period: { closed } })
      })
      await load()
    } catch (e) {
      setError(e.message || "Update failed")
    } finally {
      setSavingId(null)
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Fiscal periods</CardTitle>
      </CardHeader>
      <CardContent className="grid gap-4">
        <p className="text-xs text-muted-foreground">
          When a period is closed, journal entries and operational postings cannot use a transaction date inside that range.
        </p>
        {error ? <div className="rounded border border-red-200 bg-red-50 px-2 py-1.5 text-xs text-red-700">{error}</div> : null}
        {loading ? (
          <p className="text-xs text-muted-foreground">Loading…</p>
        ) : (
          <div className="overflow-x-auto rounded-xl border border-border/90 bg-card shadow-soft">
            <table className="min-w-full border-collapse text-xs">
              <thead className="border-b border-border bg-muted/35 text-left text-muted-foreground">
                <tr>
                  <th className="px-2 py-1.5 font-medium">Name</th>
                  <th className="px-2 py-1.5 font-medium">From</th>
                  <th className="px-2 py-1.5 font-medium">To</th>
                  <th className="px-2 py-1.5 font-medium">Closed</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((p) => (
                  <tr key={p.id} className="border-b border-border/60">
                    <td className="px-2 py-1.5">{p.name}</td>
                    <td className="px-2 py-1.5">{p.start_date}</td>
                    <td className="px-2 py-1.5">{p.end_date}</td>
                    <td className="px-2 py-1.5">
                      <label className="inline-flex items-center gap-2">
                        <input
                          type="checkbox"
                          checked={Boolean(p.closed)}
                          disabled={savingId === p.id}
                          onChange={(e) => setClosed(p.id, e.target.checked)}
                        />
                        <span className="text-muted-foreground">{savingId === p.id ? "…" : ""}</span>
                      </label>
                    </td>
                  </tr>
                ))}
                {!rows.length ? (
                  <tr>
                    <td className="px-2 py-3 text-muted-foreground" colSpan={4}>
                      No fiscal periods yet.
                    </td>
                  </tr>
                ) : null}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  )
}

function todayISODate() {
  return new Date().toISOString().slice(0, 10)
}

function firstOfMonthISODate() {
  const d = new Date()
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-01`
}

function AccountingReport({ sectionKey, sectionTitle }) {
  const [accounts, setAccounts] = useState([])
  const [loading, setLoading] = useState(false)
  usePageLoading(loading)
  const [error, setError] = useState("")
  const [result, setResult] = useState(null)
  const [asOf, setAsOf] = useState(todayISODate)
  const [glAccount, setGlAccount] = useState("")
  const [from, setFrom] = useState(firstOfMonthISODate)
  const [to, setTo] = useState(todayISODate)

  useEffect(() => {
    let alive = true
    fetchJson("/api/accounting/chart_accounts")
      .then((data) => {
        if (alive) setAccounts(data || [])
      })
      .catch(() => {})
    return () => {
      alive = false
    }
  }, [])

  async function run() {
    try {
      setLoading(true)
      setError("")
      if (sectionKey === "trial-balance") {
        const data = await fetchJson(`/api/accounting/reports/trial_balance?as_of=${encodeURIComponent(asOf)}`)
        setResult(data)
      } else if (sectionKey === "general-ledger") {
        if (!glAccount) throw new Error("Select an account")
        const data = await fetchJson(
          `/api/accounting/reports/general_ledger?chart_account_id=${encodeURIComponent(glAccount)}&from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`
        )
        setResult(data)
      } else if (sectionKey === "income-statement") {
        const data = await fetchJson(
          `/api/accounting/reports/income_statement?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`
        )
        setResult(data)
      } else if (sectionKey === "gstr1-sales") {
        const data = await fetchJson(
          `/api/accounting/reports/gstr1_sales?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`
        )
        setResult(data)
      } else if (sectionKey === "gstr2-purchases") {
        const data = await fetchJson(
          `/api/accounting/reports/gstr2_purchases?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`
        )
        setResult(data)
      }
    } catch (e) {
      setError(e.message || "Failed")
      setResult(null)
    } finally {
      setLoading(false)
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>{sectionTitle}</CardTitle>
      </CardHeader>
      <CardContent className="grid gap-4">
        {error ? <div className="rounded border border-red-200 bg-red-50 px-2 py-1.5 text-xs text-red-700">{error}</div> : null}
        <div className="flex flex-wrap items-end gap-2">
          {sectionKey === "trial-balance" ? (
            <TextInput label="As of" type="date" value={asOf} onChange={setAsOf} />
          ) : null}
          {sectionKey === "general-ledger" ? (
            <>
              <SelectInput
                label="Account"
                value={glAccount}
                onChange={setGlAccount}
                options={accounts.map((a) => ({ value: String(a.id), label: `${a.code} ${a.name}` }))}
              />
              <TextInput label="From" type="date" value={from} onChange={setFrom} />
              <TextInput label="To" type="date" value={to} onChange={setTo} />
            </>
          ) : null}
          {sectionKey === "income-statement" ? (
            <>
              <TextInput label="From" type="date" value={from} onChange={setFrom} />
              <TextInput label="To" type="date" value={to} onChange={setTo} />
            </>
          ) : null}
          {sectionKey === "gstr1-sales" ? (
            <>
              <TextInput label="From" type="date" value={from} onChange={setFrom} />
              <TextInput label="To" type="date" value={to} onChange={setTo} />
            </>
          ) : null}
          {sectionKey === "gstr2-purchases" ? (
            <>
              <TextInput label="From" type="date" value={from} onChange={setFrom} />
              <TextInput label="To" type="date" value={to} onChange={setTo} />
            </>
          ) : null}
          <Button type="button" onClick={run} disabled={loading}>
            {loading ? "…" : "Run"}
          </Button>
          {sectionKey === "gstr1-sales" ? (
            <>
              <a
                className="inline-flex h-9 items-center justify-center rounded-md border border-border bg-white px-3 text-xs font-medium text-foreground shadow-sm hover:bg-muted/40"
                href={`/api/accounting/reports/gstr1_sales.csv?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`}
              >
                Download CSV
              </a>
              <a
                className="inline-flex h-9 items-center justify-center rounded-md border border-border bg-white px-3 text-xs font-medium text-foreground shadow-sm hover:bg-muted/40"
                href={`/api/accounting/reports/gstr1_filing?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`}
                download={`gstr1-filing-${from}-${to}.json`}
              >
                Filing JSON
              </a>
            </>
          ) : null}
          {sectionKey === "gstr2-purchases" ? (
            <>
              <a
                className="inline-flex h-9 items-center justify-center rounded-md border border-border bg-white px-3 text-xs font-medium text-foreground shadow-sm hover:bg-muted/40"
                href={`/api/accounting/reports/gstr2_purchases.csv?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`}
              >
                Download CSV
              </a>
              <a
                className="inline-flex h-9 items-center justify-center rounded-md border border-border bg-white px-3 text-xs font-medium text-foreground shadow-sm hover:bg-muted/40"
                href={`/api/accounting/reports/gstr2_filing?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`}
                download={`gstr2-filing-${from}-${to}.json`}
              >
                Filing JSON
              </a>
            </>
          ) : null}
        </div>
        {result && sectionKey === "trial-balance" ? (
          <div className="overflow-x-auto rounded-xl border border-border/90 bg-card shadow-soft">
            <table className="min-w-full border-collapse text-xs">
              <thead className="border-b border-border bg-muted/35 text-left text-muted-foreground">
                <tr>
                  <th className="px-2 py-1.5 font-medium">Code</th>
                  <th className="px-2 py-1.5 font-medium">Name</th>
                  <th className="px-2 py-1.5 font-medium">Type</th>
                  <th className="px-2 py-1.5 text-right font-medium">Debit</th>
                  <th className="px-2 py-1.5 text-right font-medium">Credit</th>
                  <th className="px-2 py-1.5 text-right font-medium">Net</th>
                </tr>
              </thead>
              <tbody>
                {result.rows.map((r) => (
                  <tr key={r.chart_account_id} className="border-b border-border/60">
                    <td className="px-2 py-1">{r.code}</td>
                    <td className="px-2 py-1">{r.name}</td>
                    <td className="px-2 py-1">{r.account_type}</td>
                    <td className="px-2 py-1 text-right">{centsToAmount(r.debit_cents)}</td>
                    <td className="px-2 py-1 text-right">{centsToAmount(r.credit_cents)}</td>
                    <td className="px-2 py-1 text-right">{centsToAmount(r.net_cents)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : null}
        {result && sectionKey === "general-ledger" ? (
          <div className="overflow-x-auto rounded-xl border border-border/90 bg-card shadow-soft">
            <table className="min-w-full border-collapse text-xs">
              <thead className="border-b border-border bg-muted/35 text-left text-muted-foreground">
                <tr>
                  <th className="px-2 py-1.5 font-medium">Date</th>
                  <th className="px-2 py-1.5 font-medium">Description</th>
                  <th className="px-2 py-1.5 text-right font-medium">Debit</th>
                  <th className="px-2 py-1.5 text-right font-medium">Credit</th>
                </tr>
              </thead>
              <tbody>
                {(result.entries || []).map((e) => (
                  <tr key={e.id} className="border-b border-border/60">
                    <td className="px-2 py-1">{e.entry_date}</td>
                    <td className="px-2 py-1">{e.description}</td>
                    <td className="px-2 py-1 text-right">{centsToAmount(e.debit_cents)}</td>
                    <td className="px-2 py-1 text-right">{centsToAmount(e.credit_cents)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : null}
        {result && sectionKey === "income-statement" ? (
          <div className="grid gap-1 rounded-xl border border-border/90 bg-card shadow-sm p-2 text-xs text-foreground/85">
            <div className="flex justify-between">
              <span>Income (credit − debit)</span>
              <span className="font-medium">{centsToAmount(result.income_cents)}</span>
            </div>
            <div className="flex justify-between">
              <span>Expense (debit − credit)</span>
              <span className="font-medium">{centsToAmount(result.expense_cents)}</span>
            </div>
            <div className="flex justify-between border-t border-border pt-1 font-semibold">
              <span>Net</span>
              <span>{centsToAmount(result.net_cents)}</span>
            </div>
          </div>
        ) : null}
        {result && sectionKey === "gstr1-sales" ? (
          <div className="grid gap-3 text-xs text-foreground/85">
            <p className="text-muted-foreground">
              Company GSTIN: <span className="font-medium text-foreground">{result.organization?.gstin || "—"}</span> · State:{" "}
              <span className="font-medium text-foreground">{result.organization?.state_code || "—"}</span>
            </p>
            <div className="grid gap-1 rounded-xl border border-border/90 bg-card shadow-sm p-2 sm:grid-cols-3">
              <div>
                <div className="text-muted-foreground">Invoices (taxable / CGST / SGST / IGST)</div>
                <div className="font-mono">
                  {centsToAmount(result.totals?.invoices?.taxable_amount_cents)} / {centsToAmount(result.totals?.invoices?.cgst_cents)} /{" "}
                  {centsToAmount(result.totals?.invoices?.sgst_cents)} / {centsToAmount(result.totals?.invoices?.igst_cents)}
                </div>
              </div>
              <div>
                <div className="text-muted-foreground">Credit notes</div>
                <div className="font-mono">
                  {centsToAmount(result.totals?.credit_notes?.taxable_amount_cents)} / {centsToAmount(result.totals?.credit_notes?.cgst_cents)} /{" "}
                  {centsToAmount(result.totals?.credit_notes?.sgst_cents)} / {centsToAmount(result.totals?.credit_notes?.igst_cents)}
                </div>
              </div>
              <div>
                <div className="text-muted-foreground">Net (invoices − credits)</div>
                <div className="font-mono font-semibold">
                  {centsToAmount(result.totals?.net_after_credits?.taxable_amount_cents)} / {centsToAmount(result.totals?.net_after_credits?.cgst_cents)} /{" "}
                  {centsToAmount(result.totals?.net_after_credits?.sgst_cents)} / {centsToAmount(result.totals?.net_after_credits?.igst_cents)}
                </div>
              </div>
            </div>
            <div>
              <div className="mb-1 font-medium">By document type and GST %</div>
              <div className="max-h-40 overflow-auto rounded-xl border border-border/90 bg-card shadow-sm">
                <table className="min-w-full border-collapse text-xs">
                  <thead className="sticky top-0 border-b border-border bg-muted/35 text-left text-muted-foreground">
                    <tr>
                      <th className="px-2 py-1">Kind</th>
                      <th className="px-2 py-1 text-right">Rate %</th>
                      <th className="px-2 py-1 text-right">Taxable</th>
                      <th className="px-2 py-1 text-right">CGST</th>
                      <th className="px-2 py-1 text-right">SGST</th>
                      <th className="px-2 py-1 text-right">IGST</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(result.summary_by_rate || []).map((row, i) => (
                      <tr key={`${row.document_kind}-${row.rate_percent}-${i}`} className="border-b border-border/60">
                        <td className="px-2 py-1">{row.document_kind}</td>
                        <td className="px-2 py-1 text-right">{row.rate_percent ?? "—"}</td>
                        <td className="px-2 py-1 text-right">{centsToAmount(row.taxable_amount_cents)}</td>
                        <td className="px-2 py-1 text-right">{centsToAmount(row.cgst_cents)}</td>
                        <td className="px-2 py-1 text-right">{centsToAmount(row.sgst_cents)}</td>
                        <td className="px-2 py-1 text-right">{centsToAmount(row.igst_cents)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
            <div className="grid gap-4 md:grid-cols-2">
              <div>
                <div className="mb-1 font-medium">Invoice lines ({(result.invoice_lines || []).length})</div>
                <div className="max-h-56 overflow-auto rounded-xl border border-border/90 bg-card shadow-sm">
                  <table className="min-w-full border-collapse text-[12px]">
                    <thead className="sticky top-0 border-b border-border bg-muted/35 text-left text-muted-foreground">
                      <tr>
                        <th className="px-1 py-1">Date</th>
                        <th className="px-1 py-1">Doc</th>
                        <th className="px-1 py-1">Customer</th>
                        <th className="px-1 py-1 text-right">Taxable</th>
                        <th className="px-1 py-1 text-right">GST</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(result.invoice_lines || []).map((r) => (
                        <tr key={`inv-${r.line_id}`} className="border-b border-border/40">
                          <td className="px-1 py-0.5 whitespace-nowrap">{r.document_date}</td>
                          <td className="px-1 py-0.5">{r.document_number}</td>
                          <td className="px-1 py-0.5 max-w-[120px] truncate" title={r.customer_name}>
                            {r.customer_name}
                          </td>
                          <td className="px-1 py-0.5 text-right">{centsToAmount(r.taxable_amount_cents)}</td>
                          <td className="px-1 py-0.5 text-right whitespace-nowrap">
                            {centsToAmount(r.cgst_cents + r.sgst_cents + r.igst_cents)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
              <div>
                <div className="mb-1 font-medium">Credit note lines ({(result.credit_note_lines || []).length})</div>
                <div className="max-h-56 overflow-auto rounded-xl border border-border/90 bg-card shadow-sm">
                  <table className="min-w-full border-collapse text-[12px]">
                    <thead className="sticky top-0 border-b border-border bg-muted/35 text-left text-muted-foreground">
                      <tr>
                        <th className="px-1 py-1">Date</th>
                        <th className="px-1 py-1">Doc</th>
                        <th className="px-1 py-1">Customer</th>
                        <th className="px-1 py-1 text-right">Taxable</th>
                        <th className="px-1 py-1 text-right">GST</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(result.credit_note_lines || []).map((r) => (
                        <tr key={`cn-${r.line_id}`} className="border-b border-border/40">
                          <td className="px-1 py-0.5 whitespace-nowrap">{r.document_date}</td>
                          <td className="px-1 py-0.5">{r.document_number}</td>
                          <td className="px-1 py-0.5 max-w-[120px] truncate" title={r.customer_name}>
                            {r.customer_name}
                          </td>
                          <td className="px-1 py-0.5 text-right">{centsToAmount(r.taxable_amount_cents)}</td>
                          <td className="px-1 py-0.5 text-right whitespace-nowrap">
                            {centsToAmount(r.cgst_cents + r.sgst_cents + r.igst_cents)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        ) : null}
        {result && sectionKey === "gstr2-purchases" ? (
          <div className="grid gap-3 text-xs text-foreground/85">
            <p className="text-muted-foreground">
              Company GSTIN: <span className="font-medium text-foreground">{result.organization?.gstin || "—"}</span> · Recipient state:{" "}
              <span className="font-medium text-foreground">{result.organization?.state_code || "—"}</span>
            </p>
            <div className="grid gap-1 rounded-xl border border-border/90 bg-card shadow-sm p-2 sm:grid-cols-3">
              <div>
                <div className="text-muted-foreground">Bills (ITC in)</div>
                <div className="font-mono">
                  {centsToAmount(result.totals?.bills?.taxable_amount_cents)} / {centsToAmount(result.totals?.bills?.cgst_cents)} /{" "}
                  {centsToAmount(result.totals?.bills?.sgst_cents)} / {centsToAmount(result.totals?.bills?.igst_cents)}
                </div>
              </div>
              <div>
                <div className="text-muted-foreground">Vendor credits (ITC reversal)</div>
                <div className="font-mono">
                  {centsToAmount(result.totals?.vendor_credits?.taxable_amount_cents)} / {centsToAmount(result.totals?.vendor_credits?.cgst_cents)} /{" "}
                  {centsToAmount(result.totals?.vendor_credits?.sgst_cents)} / {centsToAmount(result.totals?.vendor_credits?.igst_cents)}
                </div>
              </div>
              <div>
                <div className="text-muted-foreground">Net (bills − credits)</div>
                <div className="font-mono font-semibold">
                  {centsToAmount(result.totals?.net_itc?.taxable_amount_cents)} / {centsToAmount(result.totals?.net_itc?.cgst_cents)} /{" "}
                  {centsToAmount(result.totals?.net_itc?.sgst_cents)} / {centsToAmount(result.totals?.net_itc?.igst_cents)}
                </div>
              </div>
            </div>
            <div>
              <div className="mb-1 font-medium">Net by GST % (bills − vendor credits)</div>
              <div className="max-h-40 overflow-auto rounded-xl border border-border/90 bg-card shadow-sm">
                <table className="min-w-full border-collapse text-xs">
                  <thead className="sticky top-0 border-b border-border bg-muted/35 text-left text-muted-foreground">
                    <tr>
                      <th className="px-2 py-1 text-right">Rate %</th>
                      <th className="px-2 py-1 text-right">Taxable</th>
                      <th className="px-2 py-1 text-right">CGST</th>
                      <th className="px-2 py-1 text-right">SGST</th>
                      <th className="px-2 py-1 text-right">IGST</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(result.summary_by_rate || []).map((row, i) => (
                      <tr key={`g2-${row.rate_percent}-${i}`} className="border-b border-border/60">
                        <td className="px-2 py-1 text-right">{row.rate_percent ?? "—"}</td>
                        <td className="px-2 py-1 text-right">{centsToAmount(row.taxable_amount_cents)}</td>
                        <td className="px-2 py-1 text-right">{centsToAmount(row.cgst_cents)}</td>
                        <td className="px-2 py-1 text-right">{centsToAmount(row.sgst_cents)}</td>
                        <td className="px-2 py-1 text-right">{centsToAmount(row.igst_cents)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
            <div className="grid gap-4 md:grid-cols-2">
              <div>
                <div className="mb-1 font-medium">Bill lines ({(result.bill_lines || []).length})</div>
                <div className="max-h-72 overflow-auto rounded-xl border border-border/90 bg-card shadow-sm">
                  <table className="min-w-full border-collapse text-[12px]">
                    <thead className="sticky top-0 border-b border-border bg-muted/35 text-left text-muted-foreground">
                      <tr>
                        <th className="px-1 py-1">Date</th>
                        <th className="px-1 py-1">Bill</th>
                        <th className="px-1 py-1">Vendor</th>
                        <th className="px-1 py-1 text-right">Taxable</th>
                        <th className="px-1 py-1 text-right">ITC</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(result.bill_lines || []).map((r) => (
                        <tr key={`bl-${r.line_id}`} className="border-b border-border/40">
                          <td className="px-1 py-0.5 whitespace-nowrap">{r.document_date}</td>
                          <td className="px-1 py-0.5">{r.document_number}</td>
                          <td className="px-1 py-0.5 max-w-[140px] truncate" title={r.vendor_name}>
                            {r.vendor_name}
                          </td>
                          <td className="px-1 py-0.5 text-right">{centsToAmount(r.taxable_amount_cents)}</td>
                          <td className="px-1 py-0.5 text-right whitespace-nowrap">
                            {centsToAmount(r.cgst_cents + r.sgst_cents + r.igst_cents)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
              <div>
                <div className="mb-1 font-medium">Vendor credit lines ({(result.vendor_credit_lines || []).length})</div>
                <div className="max-h-72 overflow-auto rounded-xl border border-border/90 bg-card shadow-sm">
                  <table className="min-w-full border-collapse text-[12px]">
                    <thead className="sticky top-0 border-b border-border bg-muted/35 text-left text-muted-foreground">
                      <tr>
                        <th className="px-1 py-1">Date</th>
                        <th className="px-1 py-1">Credit</th>
                        <th className="px-1 py-1">Vendor</th>
                        <th className="px-1 py-1 text-right">Taxable</th>
                        <th className="px-1 py-1 text-right">ITC rev.</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(result.vendor_credit_lines || []).map((r) => (
                        <tr key={`vc-${r.line_id}`} className="border-b border-border/40">
                          <td className="px-1 py-0.5 whitespace-nowrap">{r.document_date}</td>
                          <td className="px-1 py-0.5">{r.document_number}</td>
                          <td className="px-1 py-0.5 max-w-[140px] truncate" title={r.vendor_name}>
                            {r.vendor_name}
                          </td>
                          <td className="px-1 py-0.5 text-right">{centsToAmount(r.taxable_amount_cents)}</td>
                          <td className="px-1 py-0.5 text-right whitespace-nowrap">
                            {centsToAmount(r.cgst_cents + r.sgst_cents + r.igst_cents)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        ) : null}
      </CardContent>
    </Card>
  )
}

function AccountingCrud({ sectionKey, sectionTitle }) {
  const [items, setItems] = useState([])
  const [chartAccounts, setChartAccounts] = useState([])
  const [loading, setLoading] = useState(true)
  usePageLoading(loading)
  const [error, setError] = useState("")
  const [editingId, setEditingId] = useState(null)
  const [form, setForm] = useState({})

  const config = useMemo(() => {
    switch (sectionKey) {
      case "bookkeeping-settings":
        return null
      case "fiscal-periods":
        return null
      case "manual-journals":
        return {
          endpoint: "/api/accounting/manual_journals",
          wrap: "manual_journal",
          smartDefaults: (all) => ({
            journal_number: nextDocNumber(all, "journal_number", "MJ-"),
            status: "draft",
            reporting_method: "accrual_and_cash",
            lines_json: "[]"
          }),
          fields: [
            { name: "journal_date", label: "Date*", kind: "date", required: true },
            { name: "reverse_journal_date", label: "Reverse Journal Date", kind: "date" },
            { name: "publish_reverse_only", label: "Publish reverse only on reverse date", kind: "boolean" },
            { name: "journal_number", label: "Journal#*", kind: "text", required: true },
            { name: "reference_number", label: "Reference#", kind: "text" },
            { name: "notes", label: "Notes*", kind: "text" },
            {
              name: "reporting_method",
              label: "Reporting Method",
              kind: "select",
              options: [
                { value: "accrual_and_cash", label: "Accrual and Cash" },
                { value: "accrual_only", label: "Accrual Only" },
                { value: "cash_only", label: "Cash Only" }
              ]
            },
            { name: "currency", label: "Currency", kind: "text" },
            {
              name: "status",
              label: "Status",
              kind: "select",
              options: [
                { value: "draft", label: "Draft" },
                { value: "published", label: "Published" }
              ]
            }
          ],
          columns: ["journal_number", "journal_date", "reporting_method", "status"]
        }
      case "bulk-update":
        return {
          endpoint: "/api/accounting/bulk_updates",
          wrap: "bulk_update",
          fields: [
            { name: "target_resource", label: "Target Resource", kind: "text", required: true },
            { name: "target_field", label: "Target Field", kind: "text", required: true },
            { name: "from_value", label: "From Value", kind: "text" },
            { name: "to_value", label: "To Value", kind: "text" },
            { name: "effective_on", label: "Effective On", kind: "date" },
            {
              name: "status",
              label: "Status",
              kind: "select",
              options: [
                { value: "draft", label: "Draft" },
                { value: "applied", label: "Applied" }
              ]
            },
            { name: "notes", label: "Notes", kind: "text" }
          ],
          columns: ["target_resource", "target_field", "from_value", "to_value", "status"]
        }
      case "currency-adjustments":
        return {
          endpoint: "/api/accounting/currency_adjustments",
          wrap: "currency_adjustment",
          fields: [
            { name: "adjustment_date", label: "Adjustment Date", kind: "date", required: true },
            { name: "currency", label: "Currency", kind: "text", required: true },
            { name: "exchange_rate", label: "Exchange Rate", kind: "decimal", required: true },
            { name: "chart_account_id", label: "Chart Account", kind: "chart_account" },
            { name: "amount_cents", label: "Amount", kind: "amount", required: true },
            { name: "reference_number", label: "Reference #", kind: "text" },
            { name: "notes", label: "Notes", kind: "text" }
          ],
          columns: ["adjustment_date", "chart_account", "currency", "exchange_rate", "amount_cents"]
        }
      case "chart-of-accounts":
        return {
          endpoint: "/api/accounting/chart_accounts",
          wrap: "chart_account",
          fields: [
            { name: "code", label: "Code", kind: "text", required: true },
            { name: "name", label: "Name", kind: "text", required: true },
            {
              name: "account_type",
              label: "Account Type",
              kind: "select",
              options: [
                { value: "asset", label: "Asset" },
                { value: "liability", label: "Liability" },
                { value: "equity", label: "Equity" },
                { value: "income", label: "Income" },
                { value: "expense", label: "Expense" }
              ]
            },
            { name: "parent_account_id", label: "Parent Account", kind: "chart_account" },
            { name: "is_active", label: "Active", kind: "boolean" },
            { name: "balance_cents", label: "Opening Balance", kind: "amount" },
            { name: "currency", label: "Currency", kind: "text" },
            { name: "description", label: "Description", kind: "text" }
          ],
          columns: ["code", "name", "account_type", "parent_account", "is_active"]
        }
      case "budgets":
        return {
          endpoint: "/api/accounting/budgets",
          wrap: "budget",
          fields: [
            { name: "budget_name", label: "Budget Name", kind: "text", required: true },
            { name: "fiscal_year", label: "Fiscal Year", kind: "text", required: true },
            { name: "chart_account_id", label: "Chart Account", kind: "chart_account" },
            {
              name: "period",
              label: "Period",
              kind: "select",
              options: [
                { value: "annual", label: "Annual" },
                { value: "quarterly", label: "Quarterly" },
                { value: "monthly", label: "Monthly" }
              ]
            },
            { name: "start_date", label: "Start Date", kind: "date", required: true },
            { name: "end_date", label: "End Date", kind: "date", required: true },
            { name: "amount_cents", label: "Budget Amount", kind: "amount", required: true },
            { name: "currency", label: "Currency", kind: "text" },
            {
              name: "status",
              label: "Status",
              kind: "select",
              options: [
                { value: "active", label: "Active" },
                { value: "archived", label: "Archived" }
              ]
            },
            { name: "notes", label: "Notes", kind: "text" }
          ],
          columns: ["budget_name", "fiscal_year", "chart_account", "period", "amount_cents", "status"]
        }
      case "transaction-locking":
        return {
          endpoint: "/api/accounting/transaction_lockings",
          wrap: "transaction_locking",
          fields: [
            { name: "lock_from", label: "Lock From", kind: "date", required: true },
            { name: "lock_to", label: "Lock To", kind: "date" },
            { name: "active", label: "Active", kind: "boolean" },
            {
              name: "scope",
              label: "Scope",
              kind: "select",
              options: [
                { value: "all", label: "All" },
                { value: "sales", label: "Sales" },
                { value: "purchases", label: "Purchases" },
                { value: "accounting", label: "Accounting" }
              ]
            },
            { name: "reason", label: "Reason", kind: "text" }
          ],
          columns: ["lock_from", "lock_to", "scope", "active"]
        }
      default:
        return null
    }
  }, [sectionKey])

  useEffect(() => {
    let alive = true
    async function load() {
      if (REPORT_SECTIONS.has(sectionKey)) {
        setLoading(false)
        return
      }
      if (sectionKey === "bookkeeping-settings") {
        setLoading(false)
        return
      }
      if (sectionKey === "fiscal-periods") {
        setLoading(false)
        return
      }
      if (!config) {
        setLoading(false)
        return
      }
      try {
        setLoading(true)
        setError("")
        const [itemsData, accountsData] = await Promise.all([fetchJson(config.endpoint), fetchJson("/api/accounting/chart_accounts")])
        if (!alive) return
        setItems(itemsData || [])
        setChartAccounts(accountsData || [])
        setForm(config.smartDefaults ? config.smartDefaults(itemsData || []) : {})
      } catch (e) {
        if (!alive) return
        setError(e.message || "Failed to load data")
      } finally {
        if (!alive) return
        setLoading(false)
      }
    }
    load()
    return () => {
      alive = false
    }
  }, [config, sectionKey])

  function accountNameFor(id) {
    const item = chartAccounts.find((a) => String(a.id) === String(id))
    return item ? `${item.code} - ${item.name}` : ""
  }

  function resetForm() {
    setEditingId(null)
    setForm(config?.smartDefaults ? config.smartDefaults(items) : {})
  }

  function setField(name, value) {
    setForm((prev) => ({ ...prev, [name]: value }))
  }

  async function reload() {
    const data = await fetchJson(config.endpoint)
    setItems(data || [])
  }

  function hydrateForEdit(item) {
    if (sectionKey === "manual-journals") {
      setEditingId(item.id)
      setForm({
        journal_date: item.journal_date ?? "",
        reverse_journal_date: item.reverse_journal_date ?? "",
        publish_reverse_only: Boolean(item.publish_reverse_only),
        journal_number: item.journal_number ?? "",
        reference_number: item.reference_number ?? "",
        notes: item.notes ?? "",
        reporting_method: item.reporting_method ?? "accrual_and_cash",
        currency: item.currency ?? "INR",
        status: item.status ?? "draft",
        lines_json: typeof item.lines_json === "string" ? item.lines_json : JSON.stringify(item.lines_json || [])
      })
      return
    }
    const next = {}
    config.fields.forEach((f) => {
      if (f.kind === "amount") next[f.name] = centsToAmount(item[f.name])
      else if (f.kind === "chart_account") next[f.name] = item[f.name] ? String(item[f.name]) : ""
      else if (f.kind === "boolean") next[f.name] = Boolean(item[f.name])
      else next[f.name] = item[f.name] ?? ""
    })
    setEditingId(item.id)
    setForm(next)
  }

  async function onSubmit(e, forcedStatus = null) {
    if (e) e.preventDefault()
    try {
      setError("")
      const payload = {}
      if (sectionKey === "manual-journals") {
        payload.journal_date = form.journal_date || null
        payload.reverse_journal_date = form.reverse_journal_date === "" ? null : form.reverse_journal_date
        payload.publish_reverse_only = Boolean(form.publish_reverse_only)
        payload.journal_number = form.journal_number || ""
        payload.reference_number = form.reference_number === "" ? null : form.reference_number
        payload.notes = form.notes === "" ? null : form.notes
        payload.reporting_method = form.reporting_method || "accrual_and_cash"
        payload.currency = form.currency === "" || form.currency == null ? "INR" : form.currency
        payload.status = forcedStatus || form.status || "draft"
        payload.lines_json = form.lines_json != null ? String(form.lines_json) : "[]"
      } else {
        config.fields.forEach((f) => {
          const raw = form[f.name]
          if (f.kind === "amount") payload[f.name] = amountToCents(raw)
          else if (f.kind === "decimal") payload[f.name] = raw === "" ? null : Number.parseFloat(raw)
          else if (f.kind === "boolean") payload[f.name] = Boolean(raw)
          else if (f.kind === "chart_account") payload[f.name] = raw === "" ? null : Number.parseInt(raw, 10)
          else if (f.name === "currency") payload[f.name] = raw === "" || raw == null ? "INR" : raw
          else payload[f.name] = raw === "" ? null : raw
        })
        if (forcedStatus) payload.status = forcedStatus
      }

      const body = JSON.stringify({ [config.wrap]: payload })
      if (editingId) await fetchJson(`${config.endpoint}/${editingId}`, { method: "PUT", body })
      else await fetchJson(config.endpoint, { method: "POST", body })

      await reload()
      resetForm()
    } catch (err) {
      setError(err.message || "Save failed")
    }
  }

  async function onDelete(id) {
    if (!(await appConfirm("Delete this record?", { title: "Delete", danger: true, confirmLabel: "Delete" }))) return
    try {
      setError("")
      await fetchJson(`${config.endpoint}/${id}`, { method: "DELETE" })
      await reload()
      if (editingId === id) resetForm()
    } catch (err) {
      setError(err.message || "Delete failed")
    }
  }

  const chartAccountOptions = chartAccounts.map((a) => ({ value: String(a.id), label: `${a.code} - ${a.name}` }))

  if (REPORT_SECTIONS.has(sectionKey)) {
    return <AccountingReport sectionKey={sectionKey} sectionTitle={sectionTitle} />
  }

  if (sectionKey === "bookkeeping-settings") {
    return <BookkeepingSettingsPanel />
  }

  if (sectionKey === "fiscal-periods") {
    return <FiscalPeriodsPanel />
  }

  if (sectionKey === "payroll") {
    return <PayrollPanel />
  }

  if (!config) {
    return (
      <Card>
        <CardContent className="pt-6">
          <p className="text-sm text-muted-foreground">This section is not available.</p>
        </CardContent>
      </Card>
    )
  }

  return (
    <div className="flex flex-col gap-8">
      <Card id="book-primary-form">
        <CardHeader className="flex-row items-center justify-between gap-3 space-y-0">
          <CardTitle>{editingId ? `Edit ${sectionTitle}` : `Create ${sectionTitle}`}</CardTitle>
          {editingId ? (
            <Button type="button" variant="ghost" onClick={resetForm}>
              Cancel edit
            </Button>
          ) : null}
        </CardHeader>
        <CardContent>
          {error ? <div className="mb-2 rounded border border-red-200 bg-red-50 px-2 py-1.5 text-xs text-red-700">{error}</div> : null}
          <form onSubmit={onSubmit} className="grid gap-4 md:grid-cols-2">
            {sectionKey === "manual-journals" ? (
              <>
                <TextInput label="Date*" type="date" value={form.journal_date ?? ""} onChange={(v) => setField("journal_date", v)} />
                <TextInput
                  label="Reverse Journal Date"
                  type="date"
                  value={form.reverse_journal_date ?? ""}
                  onChange={(v) => setField("reverse_journal_date", v)}
                />
                <label className="md:col-span-2 flex items-center gap-2 rounded-xl border border-border/90 bg-card shadow-sm px-2 py-1.5 text-xs">
                  <input
                    type="checkbox"
                    checked={Boolean(form.publish_reverse_only)}
                    onChange={(e) => setField("publish_reverse_only", e.target.checked)}
                  />
                  <span className="text-foreground/85">Publish reverse journal only on the reverse journal date</span>
                </label>
                <TextInput label="Journal#*" value={form.journal_number ?? ""} onChange={(v) => setField("journal_number", v)} />
                <TextInput label="Reference#" value={form.reference_number ?? ""} onChange={(v) => setField("reference_number", v)} />
                <TextInput label="Notes*" value={form.notes ?? ""} onChange={(v) => setField("notes", v)} placeholder="Max. 500 characters" />
                <div className="md:col-span-2 grid gap-1.5 rounded-xl border border-border/90 bg-card shadow-sm p-2 text-xs">
                  <span className="text-muted-foreground">Reporting Method</span>
                  <div className="flex flex-wrap gap-3">
                    {[
                      { value: "accrual_and_cash", label: "Accrual and Cash" },
                      { value: "accrual_only", label: "Accrual Only" },
                      { value: "cash_only", label: "Cash Only" }
                    ].map((opt) => (
                      <label key={opt.value} className="flex items-center gap-2">
                        <input
                          type="radio"
                          name="reporting_method"
                          checked={(form.reporting_method ?? "accrual_and_cash") === opt.value}
                          onChange={() => setField("reporting_method", opt.value)}
                        />
                        <span>{opt.label}</span>
                      </label>
                    ))}
                  </div>
                </div>
                <SelectInput
                  label="Currency"
                  value={form.currency ?? "INR"}
                  onChange={(v) => setField("currency", v)}
                  options={[
                    { value: "INR", label: "INR - Indian Rupee" },
                    { value: "USD", label: "USD - US Dollar" },
                    { value: "EUR", label: "EUR - Euro" }
                  ]}
                />
                <SelectInput
                  label="Status"
                  value={form.status ?? "draft"}
                  onChange={(v) => setField("status", v)}
                  options={[
                    { value: "draft", label: "Draft" },
                    { value: "published", label: "Published" }
                  ]}
                />

                <label className="md:col-span-2 grid gap-0.5 text-xs">
                  <span className="text-muted-foreground">Lines (JSON) — debits must equal credits</span>
                  <textarea
                    rows={3}
                    value={form.lines_json ?? "[]"}
                    onChange={(e) => setField("lines_json", e.target.value)}
                    placeholder='[{"chart_account_id":1,"description":"","debit_cents":10000,"credit_cents":0},{"chart_account_id":2,"description":"","debit_cents":0,"credit_cents":10000}]'
                    className="w-full rounded-lg border border-input bg-card px-2.5 py-1.5 font-mono text-xs text-foreground outline-none transition-shadow focus:border-primary focus:ring-2 focus:ring-ring/20"
                  />
                </label>
              </>
            ) : (
              config.fields.map((f) => {
              const value = form[f.name] ?? (f.kind === "boolean" ? false : "")
              if (f.kind === "chart_account") {
                return <SelectInput key={f.name} label={f.label} value={value} onChange={(v) => setField(f.name, v)} options={chartAccountOptions} />
              }
              if (f.kind === "select") {
                return <SelectInput key={f.name} label={f.label} value={value} onChange={(v) => setField(f.name, v)} options={f.options || []} />
              }
              if (f.kind === "boolean") {
                return (
                  <label key={f.name} className="flex items-center gap-2 rounded-xl border border-border/90 bg-card shadow-sm px-2 py-1.5 text-xs">
                    <input type="checkbox" checked={Boolean(value)} onChange={(e) => setField(f.name, e.target.checked)} />
                    <span className="text-foreground/85">{f.label}</span>
                  </label>
                )
              }
              return (
                <TextInput
                  key={f.name}
                  label={f.label}
                  type={f.kind === "date" ? "date" : f.kind === "decimal" ? "number" : "text"}
                  placeholder={f.placeholder}
                  value={value}
                  onChange={(v) => setField(f.name, v)}
                />
              )
            })
            )}
            {sectionKey === "manual-journals" ? (
              <div className="md:col-span-2 flex items-center gap-2">
                <Button type="button" onClick={(e) => onSubmit(e, "published")}>
                  Save and Publish
                </Button>
                <Button type="button" variant="ghost" onClick={(e) => onSubmit(e, "draft")}>
                  Save as Draft
                </Button>
                <Button type="button" variant="ghost" onClick={resetForm}>
                  Cancel
                </Button>
              </div>
            ) : (
              <div className="md:col-span-2 flex items-center gap-2">
                <Button type="submit">{editingId ? "Save" : "Create"}</Button>
                <Button type="button" variant="ghost" onClick={reload}>
                  Refresh
                </Button>
              </div>
            )}
          </form>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex-row items-center justify-between gap-3 space-y-0">
          <CardTitle>{sectionTitle} List</CardTitle>
          <div className="text-xs text-muted-foreground">{loading ? "Loading..." : `${items.length} record(s)`}</div>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto rounded-xl border border-border/90 bg-card shadow-soft">
            <table className="min-w-full border-collapse text-xs">
              <thead>
                <tr className="border-b border-border bg-muted/35 text-left text-muted-foreground">
                  {config.columns.map((c) => (
                    <th key={c} className="px-2 py-1.5 font-medium">
                      {c.replaceAll("_", " ")}
                    </th>
                  ))}
                  <th className="px-2 py-1.5 font-medium">Actions</th>
                </tr>
              </thead>
              <tbody>
                {items.map((item) => (
                  <tr key={item.id} className="border-b border-border/60 bg-card last:border-0 hover:bg-muted/20">
                    {config.columns.map((c) => {
                      let value = item[c]
                      if (c === "chart_account") value = item.chart_account?.name || accountNameFor(item.chart_account_id)
                      if (c === "parent_account") value = item.parent_account?.name || accountNameFor(item.parent_account_id)
                      if (c.endsWith("_cents")) value = centsToAmount(value)
                      if (typeof value === "boolean") value = value ? "Yes" : "No"
                      return (
                        <td key={c} className="px-2 py-1 text-foreground/85">
                          {value ?? ""}
                        </td>
                      )
                    })}
                    <td className="px-2 py-1">
                      <div className="flex items-center gap-1">
                        <Button type="button" variant="ghost" className="h-7 px-1.5 text-xs" onClick={() => hydrateForEdit(item)}>
                          Edit
                        </Button>
                        <Button type="button" variant="ghost" className={cn("h-7 px-1.5 text-xs text-red-600 hover:text-red-700")} onClick={() => onDelete(item.id)}>
                          Delete
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
                {!loading && items.length === 0 ? (
                  <tr>
                    <td className="px-2 py-3 text-muted-foreground" colSpan={config.columns.length + 1}>
                      No records yet. Create your first one above.
                    </td>
                  </tr>
                ) : null}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}

export function AccountantPage(props) {
  const {
    section,
    section_title,
    active_slug,
    user_name,
    access_role,
    can_create_company,
    can_enter_transactions,
    can_approve_documents,
    can_manage_company_settings,
    is_viewer,
  } = props

  const access = accessFromProps(props)
  const activeKey = section || "manual-journals"

  return (
    <BookShell activeSlug={active_slug || "accountant"} userName={user_name} access={access}>
      <div className="flex flex-col gap-6">
        <PageHeader
          context="Accountant"
          title={section_title}
          actions={
            <>
              <a href="#" className="hidden text-[14px] font-medium text-primary hover:underline sm:inline">
                Find accountants
              </a>
              <Button
                type="button"
                className="gap-1 px-3"
                onClick={() => document.getElementById("book-primary-form")?.scrollIntoView({ behavior: "smooth", block: "start" })}
              >
                <span className="text-base leading-none">+</span>
                New
              </Button>
            </>
          }
        />
        <AccountingCrud sectionKey={activeKey} sectionTitle={section_title} />
      </div>
    </BookShell>
  )
}

